/**
 * The onboarding step model — one source for the wizard and the home banner.
 *
 * #2386: the banner counted five steps while the wizard's header counted six,
 * because the wizard counted its closing "You're all set" card. That card is
 * not a step anyone completes, so both surfaces count the same working steps,
 * in the wizard's order:
 *
 *   1 patient · 3 first request · 4 security · 5 voice
 *
 * Wave w1023c (Victor, 2026-10-23) retired step 2, "Build your support
 * circle", with the People address book: there is no contact list to add to.
 * The remaining steps keep their wizard numbers, so a step's number and its
 * position in the count differ from step 3 on. A stored `invite_helper` state
 * stays in Mongo and is no longer counted.
 *
 * #2385: the banner's Continue reopens the wizard at the first step that is
 * still open, which is the step the banner's "next" sentence names. It used to
 * reopen at step 1 whatever the banner said.
 */
import { useEffect, useState } from 'react';
import { VOICE_ENABLED } from '@/lib/featureFlags';
import type { FamilyOnboarding, FamilyOnboardingStep } from '@/types/api';

export type WorkingStep = 1 | 3 | 4 | 5;

/**
 * The wizard numbers of the steps a parent works through, in order. The done
 * card is not one of them.
 *
 * Ruling 2026-10-15 (Victor): while talking to the companion is switched off,
 * the voice step is skipped. A function, not a constant, because the flag is
 * read where it is used (tests flip it).
 */
export function onboardingWizardSteps(): WorkingStep[] {
  return VOICE_ENABLED ? [1, 3, 4, 5] : [1, 3, 4];
}

/** How many working steps the guide counts: four, or three while voice is off. */
export function onboardingWorkingSteps(): 3 | 4 {
  return VOICE_ENABLED ? 4 : 3;
}

/** `onboarding:banner.progress.<key>` for each working step, in wizard order. */
export type NextStepKey = 'patient' | 'firstTask' | 'mfa' | 'voice';

const STEP_KEYS: readonly NextStepKey[] = ['patient', 'firstTask', 'mfa', 'voice'];

export interface OnboardingProgress {
  /** One flag per working step, in wizard order (three while voice is off). */
  milestones: boolean[];
  /** How many working steps are done or skipped. */
  done: number;
  /** The first open step, or null when every working step is settled. */
  next: NextStepKey | null;
  /** The wizard step number of `next`, or null. */
  nextStep: WorkingStep | null;
}

const settled = (s: FamilyOnboardingStep | undefined) => Boolean(s?.done || s?.skipped);

/**
 * Where the wizard goes after the security step: the voice step, or straight
 * to the closing card while voice is off.
 */
export function stepAfterSecurity(): 5 | 6 {
  return VOICE_ENABLED ? 5 : 6;
}

export function onboardingProgress(
  onboarding: Pick<FamilyOnboarding, 'steps'>,
  { mfaEnabled = false, voiceConfigured }: { mfaEnabled?: boolean; voiceConfigured?: boolean } = {},
): OnboardingProgress {
  const steps = onboarding.steps ?? {};
  // Ruling 2026-10-15 (Victor): "Maybe later" on two-step sign-in settles the
  // step, like every other step's skip, so Continue moves past it.
  const milestones = [
    settled(steps.patient_info),
    settled(steps.first_task),
    mfaEnabled || settled(steps.security),
    ...(VOICE_ENABLED ? [voiceConfigured ?? settled(steps.voice_chosen)] : []),
  ];
  const index = milestones.findIndex((m) => !m);
  return {
    milestones,
    done: milestones.filter(Boolean).length,
    next: index === -1 ? null : STEP_KEYS[index],
    nextStep: index === -1 ? null : onboardingWizardSteps()[index],
  };
}

/**
 * Where the parent opens the app's nav at the current width (#2385). The
 * wizard's "open X" sentence and the guide chip both follow it:
 *
 * - `sidebar`: md and up, the pinned sidebar on the left.
 * - `headerMenu`: sm to md, the drawer behind the header's menu button.
 * - `bottomMore`: below sm, the drawer behind the bottom bar's More button.
 *
 * Where `matchMedia` is missing (tests, SSR) it reads as `sidebar`.
 */
export type NavPlacement = 'sidebar' | 'headerMenu' | 'bottomMore';

const MD = '(min-width: 768px)';
const SM = '(min-width: 640px)';

function readPlacement(): NavPlacement {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'sidebar';
  if (window.matchMedia(MD).matches) return 'sidebar';
  if (window.matchMedia(SM).matches) return 'headerMenu';
  return 'bottomMore';
}

export function useNavPlacement(): NavPlacement {
  const [placement, setPlacement] = useState<NavPlacement>(readPlacement);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const update = () => setPlacement(readPlacement());
    const queries = [window.matchMedia(MD), window.matchMedia(SM)];
    for (const q of queries) q.addEventListener('change', update);
    update();
    return () => {
      for (const q of queries) q.removeEventListener('change', update);
    };
  }, []);
  return placement;
}
